import {
  createMaterialTopTabNavigator,
  MaterialTopTabBarProps,
} from '@react-navigation/material-top-tabs';
import { useNavigation } from '@react-navigation/native';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Keyboard, Platform } from 'react-native';
import { InferType } from 'yup';

import Avatar from '@components/Avatar/Avatar';
import CustomTabBar from '@components/CustomTabBar/CustomTabBar';
import EmptyStateNoTeams from '@components/EmptyState/EmptyStateNoTeams';
import { ContactType } from '@components/Invite/Contact';
import ContactList from '@components/Invite/ContactList';
import ContactListMobile from '@components/Invite/ContactListMobile';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { TabNavigatorSwipeSpacer } from '@components/shared/TabNavigatorSwipeSpacer';
import { TeamListItem } from '@components/Team/TeamListItem';
import {
  ListContactUsersDocument,
  useListUsersLazyQuery,
  useUploadContactsMutation,
  User,
} from '@graphql/generated';
import { getContacts, getDeviceContactsNew } from '@hooks/useDeviceContacts';
import useFeatureFlag from '@hooks/useFeatureFlag';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import { useListTeamsUsersFromQuery } from '@hooks/useListTeamsUsersFromQuery';
import useMe from '@hooks/useMe';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { ThemedNavigationContainer } from '@navigation/ThemedNavigationContainer';
import { userSchema } from '@schemas/userSchema';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';

const TabNavigationContainer = Platform.select({
  default: ({ children }: { children: ReactNode }) => <>{children}</>,
  web: ({ children }: { children: ReactNode }) => (
    <ThemedNavigationContainer independent>
      {children}
    </ThemedNavigationContainer>
  ),
});

export type AddMemberType = InferType<typeof userSchema>;

interface File {
  __typename: string;
  id: string;
  url: string;
  cdnBaseUrl: string;
  path: string;
}

interface User {
  __typename: string;
  id: string;
  name: string;
  avatar: File | null;
}

interface Team {
  __typename: string;
  id: string;
  avatar: string;
  name: string;
  personal: boolean;
}

interface Group {
  __typename: string;
  id: string;
  name: string;
  currentUserTeamRole: string | null;
  team: Team;
  users: User[];
}

export type ListGroupData = Group[];

export interface SelectMembersListProps {
  isCreateProject?: boolean;
  isCreateTask?: boolean;
  projectUUId?: string;
  teamUUId?: string;
  contacts: User[];
  hideTeamTab?: boolean;
  hideSelectedUserList?: boolean;
  showTaskTagUsers?: boolean;
  selectedUserIds?: string[];
  disabledUsersId?: string[];
  users?: AddMemberType[];
  updateUsers: (newUsers: AddMemberType[]) => void;
  isInviting?: boolean;
  loading?: boolean;
  /** TA-10393: opt-in — swaps the blanket spinner for `ContactList`'s own
   * skeleton rows. Defaults to false so existing (project/task member)
   * callers keep the pre-existing spinner. */
  showLoadingSkeleton?: boolean;
  alphabetize?: boolean;
  listGroup?: ListGroupData[];
  setSelectTab?: (val: string) => void;
  isFromEditProject?: boolean;
  showTaskTagSupportUser?: boolean;
  isFromProjectOnly?: boolean;
  isFromGroupChat?: boolean;
}

const Tab = createMaterialTopTabNavigator();

export const SelectMembersList: React.FC<SelectMembersListProps> = ({
  isCreateProject = false,
  showTaskTagSupportUser = true,
  isCreateTask = false,
  projectUUId = '',
  disabledUsersId = [],
  teamUUId = '',
  contacts,
  hideTeamTab,
  hideSelectedUserList,
  showTaskTagUsers,
  selectedUserIds,
  users = [],
  updateUsers,
  isInviting,
  loading,
  showLoadingSkeleton = false,
  alphabetize = true,
  listGroup,
  setSelectTab,
  isFromEditProject = false,
  isFromProjectOnly = false,
  isFromGroupChat = false,
}) => {
  const APP_NAME = 'TaskTag';
  const TASKTAG_SUPPORT_EMAIL = 'emmanueln@lovettcommercial.com';
  const navigation = useNavigation();
  // TA-9872: listTeams no longer carries team users — fetch them via the
  // dedicated listTeamsUsers split query instead.
  const { teamsWithoutPersonal: teams, loading: loadingTeams } =
    useListTeamsUsersFromQuery();
  const [search, setSearch] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<string>(
    isFromGroupChat ? 'all-contacts' : 'suggested'
  );
  const [hideSuggested, setHideSuggested] = useState<boolean>(true);
  const [listUsers, { data: taskTagUsers }] = useListUsersLazyQuery();
  const { me } = useMe();
  const { listFeatureFlags } = useFeatureFlag();
  const { t } = useTranslation('models');

  // Contact sync for project/task member assignment flows
  const [uploadContacts] = useUploadContactsMutation({
    refetchQueries: [{ query: ListContactUsersDocument }],
  });

  useEffect(() => {
    if (isCreateProject || isFromEditProject || isCreateTask) {
      Platform.OS === 'android' &&
        getContacts(undefined, undefined, false, t, uploadContacts);
      Platform.OS !== 'android' &&
        getDeviceContactsNew(undefined, undefined, false, uploadContacts);
    }
  }, []);

  // Find the feature flag with the name "contact_groups"
  const contactGroupsFeature = listFeatureFlags?.find(
    (flag: { name: string; enabled: boolean }) => flag.name === 'contact_groups'
  );

  // Check if the feature is enabled
  const isContactGroupsEnabled = contactGroupsFeature?.enabled;

  const roleLabel = 'projects.roles.member';

  const [projectId, setProjectId] = useState<string>();

  const { project, loading: loadingProject } = useProjectFromQuery({
    projectUUId,
  });

  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();

  useEffect(() => {
    const avatarIds = taskTagUsers?.listUsers
      .map((u) => u.avatar?.id)
      .filter((id) => id != undefined);
    if (avatarIds && avatarIds.length > 0) checkUserAvatarURLByIds(avatarIds);
  }, [taskTagUsers?.listUsers?.length]);

  useEffect(() => {
    setProjectId(project?.id);
  }, [project]);

  const { members: projectMembers } = project || { members: [] };

  const projectUsers = () => {
    const users: User[] = [];
    projectMembers?.map((u) => {
      if (u.user.id !== me?.id) users.push(u.user);
    });
    return users;
  };

  const listProjectUsersToContactType = convertUsersToContacts(
    projectUsers()
  ).map((c) => {
    if (selectedUserIds?.includes(c.id)) {
      return {
        ...c,
        roleLabel,
      };
    } else {
      return c;
    }
  });

  const [teamId, setTeamId] = useState<string>('');

  const { team, loading: loadingTeamMembers } = useTeamFromQuery({
    teamUUId,
  });

  useEffect(() => {
    team?.id && setTeamId(team?.id);
  }, [team]);

  const { users: teamUsers } = team || { users: [] };

  useEffect(() => {
    const avatarIds = teamUsers
      ?.map((u) => u.avatar?.id)
      .filter((id) => id != undefined);
    if (avatarIds && avatarIds.length > 0) checkAvatarURLByIds(avatarIds);
  }, [teamUsers?.length]);

  const listTeamContactsToContactType = convertUsersToContacts(
    teamUsers.filter((u) => u.id !== me?.id)
  ).map((c) => {
    if (selectedUserIds?.includes(c.id)) {
      return {
        ...c,
        roleLabel,
      };
    } else {
      return c;
    }
  });

  const myTeamsUsers = () => {
    const userIds = new Set();
    // Use the team-user shape from listTeamsUsers directly; convertUsersToContacts
    // accepts this minimal shape, so no cast to the local User interface is needed.
    type TeamUser = (typeof teams)[number]['users'][number];
    const users: TeamUser[] = [];

    for (const team of teams) {
      for (const user of team.users) {
        if (user.id !== me?.id && !userIds.has(user.id)) {
          users.push(user);
          userIds.add(user.id);
        }
      }
    }

    return users;
  };

  const myTeamsUsersToContactType = convertUsersToContacts(myTeamsUsers()).map(
    (c) => {
      if (selectedUserIds?.includes(c.id)) {
        return {
          ...c,
          roleLabel,
        };
      } else {
        return c;
      }
    }
  );

  const listContactsToContactType = convertUsersToContacts(contacts).map(
    (c) => {
      if (selectedUserIds?.includes(c.id)) {
        return {
          ...c,
          roleLabel,
        };
      } else {
        return c;
      }
    }
  );

  // additionally filters out listContacts => rm filter when backend does this
  const listUsersToContactType = convertUsersToContacts(
    (taskTagUsers?.listUsers as User[]) || []
  ).filter((u) => !listContactsToContactType.some((c) => c.id === u.id));

  useEffect(() => {
    search && showTaskTagUsers && listUsers({ variables: { term: search } });
  }, [search]);

  const renderTabBar = (props: MaterialTopTabBarProps) => {
    return (
      <Box
        flexDirection='row'
        alignItems='center'
        paddingHorizontal='m'
        paddingBottom='xs'
        maxHeight={50}>
        <CustomTabBar {...props} spacing={theme.spacing.l} />
      </Box>
    );
  };

  const onPress = useCallback(
    (user: ContactType) => {
      Keyboard.dismiss();
      const newSelection: AddMemberType = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        name: user.name,
        avatar: user.avatar,
        phoneNumber: user.phoneNumber,
        email: user.email,
        type: user?.type,
        memberCount: user?.memberCount,
        users: user?.users,
      };
      if (users?.find((u) => u.id === user.id)) {
        const selectedUsers = users.filter((u) => u.id !== user.id);
        updateUsers(selectedUsers);
      } else {
        updateUsers([...users, newSelection]);
      }
    },
    [users]
  );

  useEffect(() => {
    const shouldHideSuggested =
      (teamId && listTeamContactsToContactType.length === 0) ||
      myTeamsUsersToContactType.length === 0 ||
      isFromGroupChat;

    if (shouldHideSuggested !== hideSuggested) {
      setHideSuggested(shouldHideSuggested);
    }
  }, [
    listTeamContactsToContactType.length,
    listProjectUsersToContactType.length,
    myTeamsUsersToContactType.length,
    isFromGroupChat,
  ]);

  const getLoadingState = () => {
    if (selectedTab === 'all-contacts' || search || hideTeamTab) {
      return loading;
    }
    if (teamId) {
      return loadingTeamMembers;
    }
    if (projectId) {
      return loadingProject;
    }
    return loadingTeams;
  };

  const isLoading = getLoadingState();

  if (isLoading && !showLoadingSkeleton) {
    return <ActivityIndicatorLoading />;
  }

  let contactsType: ContactType[];

  if (selectedTab === 'all-contacts' || search || hideTeamTab) {
    contactsType = listContactsToContactType;
  } else if (teamId) {
    contactsType =
      listTeamContactsToContactType?.length > 0
        ? listTeamContactsToContactType
        : listContactsToContactType;
  } else if (projectId) {
    contactsType =
      listProjectUsersToContactType?.length > 0
        ? listProjectUsersToContactType
        : listContactsToContactType;
  } else if (myTeamsUsersToContactType?.length > 0) {
    contactsType = myTeamsUsersToContactType;
  } else {
    contactsType = listContactsToContactType;
  }

  const renderContactList = () => {
    return (
      <ContactList
        shouldDisplayPhoneContactLabel={false}
        isInviting={isInviting ?? false}
        alphabetize={alphabetize}
        disabledRoles={
          isCreateProject || isCreateTask || !isFromEditProject
            ? undefined
            : [roleLabel]
        }
        disabledUsersId={disabledUsersId}
        contacts={
          showTaskTagSupportUser
            ? contactsType
            : contactsType.filter((c) => c.email !== TASKTAG_SUPPORT_EMAIL)
        }
        filterVal={search}
        onPress={onPress}
        isEditing
        loading={isLoading}
        showLoadingSkeleton={showLoadingSkeleton}
        selected={users.map((u) => u.id)}
        taskTagUsers={listUsersToContactType}
        showEmptyStateNoSearch={!!search}
        showEmptyStateNoContacts={
          !loading && listContactsToContactType.length === 0
        }
      />
    );
  };

  const renderContactGroupsList = () => {
    return (
      <ContactListMobile
        shouldDisplayPhoneContactLabel={false}
        isInviting={false}
        alphabetize={alphabetize}
        disabledRoles={
          isCreateProject || isCreateTask ? undefined : [roleLabel]
        }
        contacts={listGroup ?? []}
        filterVal={search}
        onPress={onPress}
        isEditing
        loading={isLoading}
        selected={users.map((u) => u.id)}
        taskTagUsers={listUsersToContactType}
        showEmptyStateNoSearch={!!search}
      />
    );
  };

  const getTabs = (showSuggested: boolean) => {
    return (
      <>
        <TabNavigationContainer>
          <Tab.Navigator
            screenOptions={{ swipeEnabled: Platform.OS !== 'web' }}
            sceneContainerStyle={{
              backgroundColor: 'transparent',
            }}
            tabBar={renderTabBar}
            initialRouteName={showSuggested ? 'suggested' : 'all-contacts'}
            backBehavior='none'>
            {showSuggested && (
              <Tab.Screen
                name='suggested'
                options={{
                  title: APP_NAME,
                  tabBarLabel: 'Suggested',
                  tabBarAccessibilityLabel: 'Suggested',
                }}
                listeners={{
                  tabPress: () => {
                    setSelectTab?.('suggested');
                    setSelectedTab('suggested');
                  },
                }}
                children={() => (
                  <Box flex={1} accessibilityLabel='Suggested'>
                    {renderContactList()}
                  </Box>
                )}
              />
            )}
            <Tab.Screen
              name='all-contacts'
              options={{
                title: APP_NAME,
                tabBarLabel: 'All Contacts',
                tabBarAccessibilityLabel: 'All Contacts',
              }}
              listeners={{
                tabPress: () => {
                  setSelectTab?.('all-contacts');
                  setSelectedTab('all-contacts');
                },
              }}
              children={() => (
                <Box flex={1} accessibilityLabel='All Contacts'>
                  {renderContactList()}
                </Box>
              )}
            />
            {(isFromEditProject ||
              Platform.OS === 'web' ||
              isFromGroupChat) && (
              <Tab.Screen
                name='teams'
                listeners={{
                  tabPress: () => {
                    setSelectTab?.('teams');
                  },
                }}
                options={{
                  title: APP_NAME,
                  tabBarLabel: 'Teams',
                  tabBarAccessibilityLabel: 'Teams',
                }}
                children={() => (
                  <Box accessibilityLabel='Teams' flex={1}>
                    <FlatList
                      showsVerticalScrollIndicator={false}
                      data={teams}
                      ListEmptyComponent={
                        <Box pt='xxl'>
                          <EmptyStateNoTeams />
                        </Box>
                      }
                      keyExtractor={(item) => item.id}
                      renderItem={({ item }) => (
                        <Box
                          accessibilityLabel='Team Item'
                          marginHorizontal='m'>
                          <TeamListItem
                            team={item}
                            onSelect={() => {
                              navigation.navigate('select-members-from-team', {
                                teamUUId: item.uuid,
                              });
                            }}
                          />
                        </Box>
                      )}
                      ListFooterComponent={() => (
                        <Box marginBottom='listFooter' />
                      )}
                      ListHeaderComponent={() => <Box marginTop='m' />}
                      ItemSeparatorComponent={() => <Box marginBottom='l' />}
                    />
                  </Box>
                )}
              />
            )}

            {isFromProjectOnly && isContactGroupsEnabled && (
              <Tab.Screen
                name='contact-groups'
                options={{
                  title: APP_NAME,
                  tabBarLabel: 'Contact Groups',
                  tabBarAccessibilityLabel: 'Contact Groups',
                }}
                listeners={{
                  tabPress: () => {
                    setSelectTab?.('contact-groups');
                    setSelectedTab('contact-groups');
                  },
                }}
                children={() => (
                  <Box flex={1} accessibilityLabel='contact-groups'>
                    {renderContactGroupsList()}
                  </Box>
                )}
              />
            )}
          </Tab.Navigator>
        </TabNavigationContainer>
        <TabNavigatorSwipeSpacer />
      </>
    );
  };

  const getContactList = () => {
    if (!hideTeamTab && !search) {
      return getTabs(!hideSuggested);
    }
    return <>{renderContactList()}</>;
  };

  return (
    <Box flex={1}>
      <Box
        marginHorizontal='m'
        marginTop='m'
        flexDirection='row'
        alignItems='center'>
        <SearchInput
          onTextChanged={(t: string) => setSearch(t)}
          value={search}
        />
      </Box>
      <Box>
        {users.length && !hideSelectedUserList ? (
          <FlatList
            style={{
              marginTop: theme.spacing.m,
              paddingBottom: theme.spacing.m,
            }}
            horizontal
            data={users?.filter((item) => item.role !== 'OWNER')}
            ItemSeparatorComponent={() => <Box marginRight='m' />}
            ListHeaderComponent={() => <Box marginRight='m' />}
            ListFooterComponent={() => <Box marginRight='m' />}
            renderItem={({ item, index }) => {
              const { id, avatar, memberCount, name, firstName } = item;
              return (
                <Box key={index} alignItems='center' alignSelf='flex-start'>
                  <Avatar
                    id={item.id}
                    avatar={avatar}
                    label={name}
                    isMultiple={item?.type === 'GROUP'}
                    onDismiss={() => {
                      const newUsers = users.filter((i) => i.id !== id);
                      updateUsers(newUsers);
                    }}
                  />
                  {firstName && (
                    <Text
                      variant='labelSmall'
                      marginTop='xs'
                      style={{ maxWidth: 64 }}
                      numberOfLines={1}>
                      {firstName}
                    </Text>
                  )}
                  {!firstName && name && (
                    <Text
                      variant='labelSmall'
                      marginTop='xs'
                      style={{ maxWidth: 64 }}
                      numberOfLines={1}>
                      {name}
                    </Text>
                  )}
                  {!firstName && memberCount && (
                    <Text
                      variant='labelSmall'
                      marginTop='xs'
                      style={{ maxWidth: 100 }}
                      numberOfLines={1}>
                      {memberCount + ' members'}
                    </Text>
                  )}
                </Box>
              );
            }}
          />
        ) : (
          <Box marginTop='m' />
        )}
      </Box>
      <Box flex={1} style={{ height: `100%` }}>
        {getContactList()}
      </Box>
    </Box>
  );
};
